<template>
  <div class="aggregate-hospital-map">
    <MglMap
      :accessToken="accessToken"
      :mapStyle="mapStyle"
      :center.sync="center"
      :zoom.sync="zoom"
      :attributionControl="false"
      @load="onMapLoaded"
      @dragend="onDrag"
      @zoomend="onZoom"
    >
      <component
        v-for="item in dataset"
        :key="item.id"
        :is="getMarkerComponent(item.level)"
        :dataset="item"
        :resource="resource"
      />
      <MglNavigationControl :showCompass="false" position="bottom-right" />
    </MglMap>
  </div>
</template>

<script>
import AggregateHospitalMarker from '@/components/HospitalMap/aggregate-hospital-marker';
import IndividualHospitalMarker from '@/components/HospitalMap/individual-hospital-marker';
import Mapbox from 'mapbox-gl';
import axios from 'axios';
import {
  AggregateGranularity,
  AggregateChartColors,
  CapacityCategories,
  HospitalResources,
} from '@/store/types';
import { Api } from '@/store/api';
import { MglMap, MglNavigationControl } from 'vue-mapbox';

const DATA_KEY = 'stateData';
const LAYER_KEY = 'stateLayer';

// Creates a Mapbox data source by merging coordinate geometry from geojson with
// resource capacity data from the API.
const getStateDataSource = (dataset, geojson) => {
  if (!dataset.length) {
    return null;
  }
  const getDominantCapacity = (stateData, resourceKey) => {
    if (!stateData) return null;
    const resources = stateData[resourceKey];
    // Check to see if the majority of the state has no_data. In which case,
    // just return "no_data".
    const noData = resources[CapacityCategories.NO_DATA.toLowerCase()];
    const total = Object.values(resources).reduce(
      (total, val) => total + val,
      0,
    );
    if (noData / total >= 0.5) {
      return CapacityCategories.NO_DATA;
    } else {
      // For states where the majority of the hospitals have data, return
      // the most dominant category that isn't `no_data`.
      const result = Object.keys(CapacityCategories)
        .slice(1)
        .reduce((result, key) => {
          const newVal = resources[key.toLowerCase()];
          const oldVal = resources[result.toLowerCase()];
          return newVal > oldVal ? key : result;
        }, CapacityCategories.UNDER_CAPACITY);
      return result;
    }
  };
  return Object.freeze({
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: geojson.features.map(item => {
        const state = dataset.find(
          state => state.name === item.properties.name,
        );
        const resources = Object.keys(HospitalResources).reduce((obj, k) => {
          const key = k.toLowerCase();
          return {
            ...obj,
            [key]: getDominantCapacity(state, key),
          };
        }, {});
        return {
          type: 'Feature',
          geometry: item.geometry,
          properties: {
            ...resources,
            name: item.properties.name,
          },
        };
      }),
    },
  });
};

export default {
  components: { MglMap, MglNavigationControl },
  props: {
    resource: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: process.env.VUE_APP_MAPBOX_STYLE,
      center: [-98.5795, 39.8283], // Center of continental US
      dataset: [],
      hasLoadedGeojson: false,
      isMapInitialized: false,
      zoom: 2,
    };
  },
  computed: {
    granularity() {
      if (this.zoom > 8) {
        return AggregateGranularity.HOSPITAL;
      }
      if (this.zoom > 7) {
        return AggregateGranularity.CITY;
      }
      if (this.zoom > 6) {
        return AggregateGranularity.COUNTY;
      }
      return AggregateGranularity.STATE;
    },
  },
  watch: {
    granularity(newVal) {
      if (newVal === AggregateGranularity.STATE) {
        this.updateStateLayer();
      } else if (this.map.getLayer(LAYER_KEY)) {
        this.map.removeLayer(LAYER_KEY);
      }
    },
    hasLoadedGeojson() {
      this.updateStateLayer();
    },
    resource() {
      this.updateStateLayer();
    },
  },
  created() {
    this.mapbox = Mapbox;
    this.map = null;
  },
  methods: {
    updateStateLayer() {
      if (
        !this.map ||
        !this.hasLoadedGeojson ||
        this.granularity !== AggregateGranularity.STATE
      )
        return;
      if (this.map.getLayer(LAYER_KEY)) {
        this.map.removeLayer(LAYER_KEY);
      }
      this.map.addLayer({
        id: LAYER_KEY,
        type: 'fill',
        source: DATA_KEY,
        paint: {
          'fill-color': [
            'match',
            ['get', this.resource],

            CapacityCategories.UNDER_CAPACITY,
            AggregateChartColors.UNDER_CAPACITY,

            CapacityCategories.NEAR_CAPACITY,
            AggregateChartColors.NEAR_CAPACITY,

            CapacityCategories.AT_CAPACITY,
            AggregateChartColors.AT_CAPACITY,

            CapacityCategories.OVER_CAPACITY,
            AggregateChartColors.OVER_CAPACITY,

            'transparent',
          ],
          'fill-opacity': 0.25,
        },
      });
    },
    async onMapLoaded(event) {
      this.map = event.map;
      this.fitMapToUS();
      const [stateDataset, geojson] = await Promise.all([
        this.fetchDataset(),
        (await axios.get('/us_states.geojson')).data,
      ]);
      this.map.addSource(DATA_KEY, getStateDataSource(stateDataset, geojson));
      this.hasLoadedGeojson = true;
      this.isMapInitialized = true;
    },
    async fetchDataset() {
      const bounds =
        this.map && this.granularity !== AggregateGranularity.STATE
          ? this.map.getBounds().toArray()
          : undefined;
      const results = await Api.loadAggregateStatuses(this.granularity, bounds);
      this.dataset = results;
      return results;
    },
    fitMapToUS() {
      // Fit the map to the continental US
      this.map.fitBounds(
        [
          [-124.848974, 24.396308],
          [-66.885444, 49.384358],
        ],
        {
          padding: 32,
        },
      );
    },
    getMarkerComponent(level) {
      return level === AggregateGranularity.HOSPITAL
        ? IndividualHospitalMarker
        : AggregateHospitalMarker;
    },
    onDrag() {
      this.fetchDataset();
    },
    async onZoom() {
      if (!this.isMapInitialized) return;
      await this.$nextTick();
      this.fetchDataset();
    },
  },
};
</script>
